// frontend/src/utils/nameClaim.js
//
// Link-in-bio claim helpers for the name-generator pages (Growth round, T21).
//
// THE RULES ARE A COPY OF backend/utils/bioUsername.js, ON PURPOSE. The browser
// works out which greatlysocial.com/u/<name> each result would claim so it can
// skip names that can never be valid (too long, reserved) without a request.
// The server still decides: GET /api/public/bio/check runs the same rules and
// sign-up re-checks the name. nameClaim.test.js runs both copies side by side,
// so if one changes without the other a test fails.
//
// THE SHORTLIST lives in localStorage under SHORTLIST_KEY, shared by every
// generator page, and is cleaned the way backend/services/nameClaim.js cleans
// it at sign-up (trimmed, 60 chars, no case-insensitive duplicates, 50 max).
// The sign-up page reads it from here; the claim itself travels in the URL:
//   /register?claim=<username>&from=<generator slug>[&kit=<profile kit id>]

export const RESERVED = [
  'api', 'u', 'go', 'dashboard', 'admin', 'login', 'register', 'app',
  'www', 'settings', 'billing', 'static', 'assets', 'auth', 'public',
]

export const SHORTLIST_KEY = 'gs_name_shortlist'
export const SHORTLIST_MAX = 50
export const CHECK_BATCH = 12          // the endpoint refuses more per call
export const BIO_HOST = 'greatlysocial.com/u/'

/** 'Soft.Luna' → 'softluna', 'The Luna Hour' → 'thelunahour'. */
export function claimNameFor (raw) {
  return String(raw || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '')
}

/** True when the backend's validateUsername would accept it. */
export function isClaimable (username) {
  return /^[a-z0-9_-]{3,30}$/.test(username) && !RESERVED.includes(username)
}

export function cleanShortlist (list) {
  if (!Array.isArray(list)) return []
  const seen = new Set()
  const out = []
  for (const item of list) {
    if (typeof item !== 'string') continue
    const name = item.trim().slice(0, 60)
    if (!name || seen.has(name.toLowerCase())) continue
    seen.add(name.toLowerCase())
    out.push(name)
    if (out.length === SHORTLIST_MAX) break
  }
  return out
}

// localStorage can be missing or throw (private mode, blocked storage). The
// shortlist then simply lasts until the page is closed.
export function readShortlist () {
  try {
    return cleanShortlist(JSON.parse(window.localStorage.getItem(SHORTLIST_KEY) || '[]'))
  } catch { return [] }
}

export function writeShortlist (list) {
  const clean = cleanShortlist(list)
  try {
    if (clean.length) window.localStorage.setItem(SHORTLIST_KEY, JSON.stringify(clean))
    else window.localStorage.removeItem(SHORTLIST_KEY)
  } catch { /* storage blocked */ }
  return clean
}

/** Where every Claim / Save-it / Unlock-it link goes. `kit` is an AI profile kit id (T23). */
export function signupHref ({ claim, from, kit } = {}) {
  const q = new URLSearchParams()
  if (claim) q.set('claim', claim)
  if (from) q.set('from', from)
  if (kit) q.set('kit', kit)
  const s = q.toString()
  return s ? `/register?${s}` : '/register'
}

/**
 * Check link-in-bio names in batches of CHECK_BATCH.
 * Resolves to { [username]: { available, reason? } }. A batch that fails
 * (network, 429, 500) is left out, so those names just show no claim line.
 */
export async function checkClaimNames (usernames, fetchImpl) {
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null)
  const names = [...new Set(usernames.filter(isClaimable))]
  const out = {}
  if (!doFetch || !names.length) return out
  const batches = []
  for (let i = 0; i < names.length; i += CHECK_BATCH) batches.push(names.slice(i, i + CHECK_BATCH))
  await Promise.all(batches.map(async batch => {
    try {
      const r = await doFetch(`/api/public/bio/check?names=${encodeURIComponent(batch.join(','))}`, {
        credentials: 'include',
      })
      if (!r.ok) return
      const data = await r.json()
      for (const row of (data && data.results) || []) {
        if (!row || typeof row.username !== 'string') continue
        out[row.username] = { available: !!row.available, reason: row.reason || '' }
      }
    } catch { /* leave this batch unknown */ }
  }))
  return out
}
